import { lazy, Suspense, useEffect } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { PageLoader } from './components/ui';
import { refreshSession } from './lib/queries';
import { useSession } from './lib/session';
import { isLocale, useLocale } from './lib/i18n';
import {
  ForgotPasswordPage,
  InvitePage,
  LoginPage,
  NoWorkspacePage,
  ResetPasswordPage,
  SignupPage,
} from './pages/auth/AuthPages';
import { localizedPath } from '@bannermatic/core';
import { PUBLIC_PAGES } from './marketing/pages';
import { PublicRoute } from './marketing/PublicRoute';
import { CookieConsent } from './components/CookieConsent';

const EditorPage = lazy(() =>
  import('./editor/EditorPage').then((m) => ({ default: m.EditorPage })),
);
const TemplatesPage = lazy(() =>
  import('./pages/app/Templates').then((m) => ({ default: m.TemplatesPage })),
);
const AutomationsPage = lazy(() =>
  import('./pages/app/Automations').then((m) => ({ default: m.AutomationsPage })),
);
const AutomationEditorPage = lazy(() =>
  import('./pages/app/AutomationEditor').then((m) => ({ default: m.AutomationEditorPage })),
);
const AdminPage = lazy(() => import('./pages/app/Admin').then((m) => ({ default: m.AdminPage })));
const BatchPage = lazy(() => import('./pages/app/Batch').then((m) => ({ default: m.BatchPage })));
const RendersPage = lazy(() =>
  import('./pages/app/Renders').then((m) => ({ default: m.RendersPage })),
);
const BrandsPage = lazy(() =>
  import('./pages/app/Brands').then((m) => ({ default: m.BrandsPage })),
);
const AssetsPage = lazy(() =>
  import('./pages/app/Assets').then((m) => ({ default: m.AssetsPage })),
);
const FontsPage = lazy(() => import('./pages/app/Fonts').then((m) => ({ default: m.FontsPage })));
const ApiPage = lazy(() => import('./pages/app/ApiPage').then((m) => ({ default: m.ApiPage })));
const IntegrationsPage = lazy(() =>
  import('./pages/app/Integrations').then((m) => ({ default: m.IntegrationsPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/app/Settings').then((m) => ({ default: m.SettingsPage })),
);

/** Loads the session once; public pages render right away, the rest waits in SessionLoaded. */
function SessionGate() {
  const user = useSession((s) => s.user);
  const setLocale = useLocale((s) => s.setLocale);
  useEffect(() => {
    void refreshSession().then((me) => {
      if (isLocale(me?.user?.locale) && !localStorage.getItem('bm.locale'))
        setLocale(me.user.locale);
    });
  }, [setLocale]);
  useEffect(() => {
    document.documentElement.lang = useLocale.getState().locale;
  }, [user]);
  return (
    <>
      <Outlet />
      <CookieConsent />
    </>
  );
}

/** Routes that depend on who is signed in. */
function SessionLoaded() {
  const loaded = useSession((s) => s.loaded);
  if (!loaded) return <PageLoader />;
  return <Outlet />;
}

/** Each public page under every language it exists in: "/terms", "/uk/terms", … */
const publicRoutes = PUBLIC_PAGES.flatMap((page) =>
  page.locales.map((locale) => ({
    path: localizedPath(locale, page.path),
    element: <PublicRoute page={page} locale={locale} />,
  })),
);

function RequireAuth() {
  const user = useSession((s) => s.user);
  const workspaceId = useSession((s) => s.workspaceId);
  const location = useLocation();
  if (!user)
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  if (!workspaceId) return <NoWorkspacePage />;
  return (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  );
}

function GuestOnly() {
  const user = useSession((s) => s.user);
  const location = useLocation();
  const next = new URLSearchParams(location.search).get('next');
  if (user) return <Navigate to={next && next.startsWith('/') ? next : '/app/templates'} replace />;
  return <Outlet />;
}

const router = createBrowserRouter([
  {
    element: <SessionGate />,
    children: [
      ...publicRoutes,
      {
        element: <SessionLoaded />,
        children: [
          {
            element: <GuestOnly />,
            children: [
              { path: '/login', element: <LoginPage /> },
              { path: '/signup', element: <SignupPage /> },
              { path: '/forgot-password', element: <ForgotPasswordPage /> },
            ],
          },
          { path: '/reset-password', element: <ResetPasswordPage /> },
          { path: '/invite/:token', element: <InvitePage /> },
          {
            element: <RequireAuth />,
            children: [
              { path: '/app/templates/:id/edit', element: <EditorPage /> },
              {
                path: '/app',
                element: <AppLayout />,
                children: [
                  { index: true, element: <Navigate to="/app/templates" replace /> },
                  { path: 'templates', element: <TemplatesPage /> },
                  { path: 'automations', element: <AutomationsPage /> },
                  { path: 'automations/:id', element: <AutomationEditorPage /> },
                  { path: 'batches/:id', element: <BatchPage /> },
                  { path: 'renders', element: <RendersPage /> },
                  { path: 'assets', element: <AssetsPage /> },
                  { path: 'brands', element: <BrandsPage /> },
                  { path: 'fonts', element: <FontsPage /> },
                  { path: 'api', element: <ApiPage /> },
                  { path: 'integrations', element: <IntegrationsPage /> },
                  { path: 'settings', element: <Navigate to="/app/settings/general" replace /> },
                  { path: 'settings/:tab', element: <SettingsPage /> },
                  { path: 'admin', element: <AdminPage /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
